import { PnlCategoriesEnum } from "types/enums";
import {
  Button,
  Checkbox,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  startTransition as reactStartTransition,
  TransitionStartFunction,
} from "react";
import {
  darkModeModalBackgroundColor,
  darkModeTextColor,
  lightNeutral70,
  lightTextBody,
} from "constants/colors";
import {
  CATEGORY_TO_LABEL_MAP,
  PNL_COLORS,
  useBreakpoints,
} from "constants/constants";
import {
  ArrowDownward,
  ArrowRight,
  ArrowUpward,
  Close,
  FilterList,
  KeyboardArrowDown,
  Search,
  Sell,
  Sort,
} from "@mui/icons-material";
import { useIsDarkMode } from "store/atoms";
import {
  INITIAL_PROCESS_TILES_FILTER_STATE,
  ProcessTilesFilterState,
  SORTABLE_METRICS,
  SortableMetric,
  useProcessTilesFilterState,
} from "./useProcessTilesFilterState";
import { isEqual, kebabCase } from "lodash";
import { useSelector } from "store/useSelector";
import { useVisibleProcesses } from "utils/customHooks/genericHooks";
import SearchMultiSelect from "./SearchMultiSelect";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { GroupByDropdown } from "./GroupByDropdown";
import {
  ProcessTilesFilterControlsStyles,
  StyledMenuItemFilterControls,
  StyledOption,
} from "./ProcessTilesFilterControlsStyles";
import FilterByTagDropdownItems from "components/FlightTrends/TrendsFilters/FilterByTagDropdown";
import { TagDetails } from "types/types";
import { useExpandedTileFlags } from "components/Dashboard/Tiles/ProcessTile/processTileUtils";
import { useTopLosses, LossItemDetails } from "./useTopLosses";
import { PNL_COLOR_SQUARE_BORDER } from "components/Nav/ColorLegend";
import { createGlobalStyle } from "styled-components/macro";
import { atom, useAtom } from "jotai";
import { useTagList } from "components/FlightTrends/TrendsFilters/useTagList";
import { lightGreyBackground } from "reports/constants/colors";
const SELECT_ALL = "SELECT_ALL";

// Jotai atom to track which menu is currently open
const activeMenuAtom = atom<"topLosses" | "tags" | null>(null);

/** Filter controls for process tiles, like "status", "sort by duration", "search for a process" */
export function ProcessTilesFilterControls() {
  const [isDarkMode] = useIsDarkMode();
  const { isTabletOrLarger } = useBreakpoints();
  const [isPending, startTransition] = useTransition();

  // change opacity of process tiles while pending
  useLayoutEffect(() => {
    const processTilesContainer = document.querySelector(
      `[data-component-name="Process Tiles Panel"]`
    );
    if (!processTilesContainer) {
      return;
    }
    if (isPending) {
      processTilesContainer.classList.add("pending");
    } else {
      processTilesContainer.classList.remove("pending");
    }
  }, [isPending]);

  return (
    <ProcessTilesFilterControlsStyles
      $isDarkMode={isDarkMode}
      $isPending={isPending}
    >
      {isTabletOrLarger && (
        <ProcessTilesSortFilterGroupByDropdowns
          startTransition={startTransition}
        />
      )}
      <SearchProcessNamesInput startTransition={startTransition} />
    </ProcessTilesFilterControlsStyles>
  );
}

export function ProcessTilesSortFilterGroupByDropdowns({
  startTransition,
}: {
  startTransition?: TransitionStartFunction;
} = {}) {
  return (
    <>
      <SortByDropdown
        startTransition={startTransition ?? reactStartTransition}
      />
      <FilterDropdown
        startTransition={startTransition ?? reactStartTransition}
      />
      <GroupByDropdown
        startTransition={startTransition ?? reactStartTransition}
      />
    </>
  );
}

function FilterDropdown({ startTransition }) {
  const [filterState, setFilterState] = useProcessTilesFilterState();
  const { translate } = useFlightTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = event => {
    setAnchorEl(p => (p ? null : event.currentTarget));
  };
  const [, setActiveMenu] = useAtom(activeMenuAtom);
  const handleClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };
  const [isDarkMode] = useIsDarkMode();
  const active = !isEqual(
    filterState.filterByCategory,
    INITIAL_PROCESS_TILES_FILTER_STATE.filterByCategory
  );

  return (
    <>
      <Button
        aria-controls="process-tiles-filter-controls"
        aria-haspopup="true"
        className={`btnProcessTilesDropdown${active ? " active" : ""}`}
        onClick={handleClick}
        endIcon={<KeyboardArrowDown />}
        startIcon={<FilterList />}
      >
        {translate(`Filter`)}
      </Button>
      <Menu
        id="process-tiles-filter-controls"
        anchorEl={anchorEl}
        keepMounted={false}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock={true}
        {...(isDarkMode
          ? {
              PaperProps: {
                style: {
                  background: darkModeModalBackgroundColor,
                  color: darkModeTextColor,
                },
              },
            }
          : {})}
        variant={"menu"} // Use "menu" to prevent selected items from impacting the initial focus and the vertical alignment relative to the anchor element.
      >
        <StyledMenuItemFilterControls
          className="selectAll"
          value={"Select All"}
          style={{ fontWeight: "bold" }}
          onClick={() => {
            startTransition(() => {
              setFilterState(p => ({
                ...p,
                filterByCategory: p.filterByCategory.includes(SELECT_ALL)
                  ? []
                  : INITIAL_PROCESS_TILES_FILTER_STATE.filterByCategory,
              }));
            });
          }}
        >
          {translate("Select All")}
          <Checkbox
            style={{ marginLeft: "auto" }}
            checked={filterState.filterByCategory.includes(SELECT_ALL)}
          />
        </StyledMenuItemFilterControls>
        {Object.entries(CATEGORY_TO_LABEL_MAP).map(
          ([category, categoryLabel]) => (
            <StyledMenuItemFilterControls
              key={category}
              value={category}
              onClick={() => {
                startTransition(() => {
                  setFilterState(p => {
                    const checked = p.filterByCategory.includes(
                      category as PnlCategoriesEnum
                    );
                    return {
                      ...p,
                      filterByCategory: checked
                        ? p.filterByCategory.filter(
                            c => c !== category && c !== SELECT_ALL
                          )
                        : [
                            ...p.filterByCategory,
                            category as PnlCategoriesEnum,
                          ],
                    };
                  });
                });
              }}
            >
              <div
                className="circle"
                style={{ background: PNL_COLORS[category] }}
              ></div>{" "}
              <div className="label">{categoryLabel}</div>{" "}
              <Checkbox
                checked={filterState.filterByCategory.includes(
                  category as PnlCategoriesEnum
                )}
              />
            </StyledMenuItemFilterControls>
          )
        )}
        <Divider />
        <NestedMenuItemTagPicker {...{ startTransition }} />
        <Divider />
        <NestedMenuItemFilterTopLosses {...{ startTransition }} />
      </Menu>
    </>
  );
}

function NestedMenuItemTagPicker({
  startTransition,
}: {
  startTransition: TransitionStartFunction;
}) {
  const [filterState, setFilterState] = useProcessTilesFilterState();
  const { translate } = useFlightTranslation();
  const [activeMenu, setActiveMenu] = useAtom(activeMenuAtom);

  const closeAllMenus = useCallback(() => {
    setActiveMenu(null);
  }, [setActiveMenu]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Close other menus and open this one
    closeAllMenus();
    setActiveMenu("tags");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (activeMenu === "tags") {
      setActiveMenu(null);
    }
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    // Close any other open menus when hovering on this item
    if (activeMenu && activeMenu !== "tags") {
      closeAllMenus();
    }

    const targetElement = event.currentTarget;

    // Clear any existing timer
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    // Set timer to open after short delay
    hoverTimerRef.current = setTimeout(() => {
      setActiveMenu("tags");
      setAnchorEl(targetElement);
    }, 300);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  const tagList: TagDetails[] = useTagList();

  const isAllSelected = filterState.filterByTagNames?.length === tagList.length;

  return (
    <>
      <StyledMenuItemFilterControls
        className="selectATag"
        value={"Filter by Tags"}
        style={{
          fontWeight: "bold",
          ...(activeMenu === "tags" && {
            backgroundColor: lightGreyBackground,
          }),
        }}
        onClick={e => {
          e.stopPropagation();
          handleClick(e);
        }}
        onMouseEnter={handleMouseEnter}
      >
        <div
          style={{
            margin: "0 10px 0 -2px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Sell style={{ width: 18, color: lightNeutral70 }} />
        </div>
        <div className="label">{translate("Filter by Tags...")}</div>
        <ArrowRight style={{ marginLeft: "auto" }} />
      </StyledMenuItemFilterControls>
      {anchorEl && <MouseoverMenuItemGlobalStyles />}
      <Menu
        id="process-tiles-tag-menu"
        hideBackdrop={true}
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && activeMenu === "tags"}
        onClose={handleClose}
        MenuListProps={{
          onMouseEnter: () => {
            /* prevent close during hover transition */
          },
          style: { pointerEvents: "auto" },
        }}
        disableAutoFocusItem
        keepMounted
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {/* All tags option at the top */}
        <MenuItem
          value={"All"}
          onClick={() => {
            startTransition(() => {
              setFilterState(p => {
                return {
                  ...p,
                  filterByTagNames: isAllSelected
                    ? []
                    : tagList.map(tag => tag.name),
                };
              });
            });
            // Don't call handleClose to keep menu open
          }}
          style={{ display: "flex", alignItems: "center" }}
        >
          <div style={{ flexGrow: 1 }}>All</div>
          <div className="check-box">
            <Checkbox
              checked={isAllSelected}
              indeterminate={Boolean(
                filterState.filterByTagNames !== null &&
                  filterState.filterByTagNames.length > 0
              )}
              size="small"
              style={{ padding: "0 0 0 16px", marginRight: -9 }}
            />
          </div>
        </MenuItem>
        <Divider style={{ marginTop: 0, marginBottom: 0 }} />
        <FilterByTagDropdownItems
          handleClose={handleClose}
          onTagSelect={(tag: TagDetails) => {
            startTransition(() => {
              setFilterState(p => {
                // Handle null state (no filter) separately
                if (p.filterByTagNames === null) {
                  // If starting from null state, select just this tag
                  return {
                    ...p,
                    filterByTagNames: [tag.name],
                  };
                }

                const currentTagFilters = p.filterByTagNames;
                const isTagSelected = currentTagFilters.includes(tag.name);

                // If all tags selected (empty array)
                if (currentTagFilters.length === 0) {
                  // Select just this tag
                  return {
                    ...p,
                    filterByTagNames: [tag.name],
                  };
                }
                // If this tag is already selected, remove it
                else if (isTagSelected) {
                  const newFilters = currentTagFilters.filter(
                    name => name !== tag.name
                  );
                  // If no tags left, set to null to indicate no filter
                  if (newFilters.length === 0) {
                    return {
                      ...p,
                      filterByTagNames: null,
                    };
                  }
                  return {
                    ...p,
                    filterByTagNames: newFilters,
                  };
                }
                // Otherwise add this tag
                else {
                  return {
                    ...p,
                    filterByTagNames: [...currentTagFilters, tag.name],
                  };
                }
              });
            });
          }}
        />
      </Menu>
    </>
  );
}

function NestedMenuItemFilterTopLosses({
  startTransition,
}: {
  startTransition: TransitionStartFunction;
}) {
  const [filterState, setFilterState] = useProcessTilesFilterState();
  const { isTopLossesAllowed } = useExpandedTileFlags(null);
  const { translate } = useFlightTranslation();
  const [activeMenu, setActiveMenu] = useAtom(activeMenuAtom);

  const closeAllMenus = useCallback(() => {
    setActiveMenu(null);
  }, [setActiveMenu]);
  const [
    {
      general: { expandedProcessTileTopLosses },
    },
  ] = useFlightConfig();

  // Show this component only if the necessary flags are true
  const showTopLossesFilter = expandedProcessTileTopLosses;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isTopLossesAllowed) {
      return;
    }
    // Close other menus and open this one
    closeAllMenus();
    setActiveMenu("topLosses");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (activeMenu === "topLosses") {
      setActiveMenu(null);
    }
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (!isTopLossesAllowed) {
      return;
    }

    // Close any other open menus when hovering on this item
    if (activeMenu && activeMenu !== "topLosses") {
      closeAllMenus();
    }

    const targetElement = event.currentTarget;

    // Clear any existing timer
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    // Set timer to open after short delay
    hoverTimerRef.current = setTimeout(() => {
      setActiveMenu("topLosses");
      setAnchorEl(targetElement);
    }, 300);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  // Get top losses dynamically from all visible processes, grouped by category
  const { byCategory, categories } = useTopLosses();

  // Get all loss names from all categories for the "All" checkbox logic
  const allLossNames = useMemo(() => {
    return categories.flatMap(cat => {
      return (byCategory[cat] || []).map(item => item.name);
    });
  }, [byCategory, categories]);

  // Check if all losses are selected
  const isAllSelected = useMemo(() => {
    if (filterState.filterByTopLosses === null) {
      return false; // Starting state with nothing selected
    }

    if (filterState.filterByTopLosses.length === 0) {
      return true; // Empty array means all selected
    }

    // Otherwise check if there are the same number of selected losses as total losses
    return (
      filterState.filterByTopLosses.length === allLossNames.length &&
      allLossNames.every(loss => filterState.filterByTopLosses?.includes(loss))
    );
  }, [filterState.filterByTopLosses, allLossNames]);

  const tagList = useTagList();
  const filteredTagList = useMemo(() => {
    // Filter out 'Producing' and 'Unknown' and 'Uncollected' tags
    const filteredTagList = tagList.filter(
      tag => !["Producing", "Unknown", "Uncollected"].includes(tag.name)
    );

    return filteredTagList.sort((tagA, tagB) =>
      tagA.name > tagB.name ? 1 : -1
    );
  }, [tagList]);

  if (!showTopLossesFilter) {
    return null;
  }
  // Check if a loss name is selected in the filter
  const isLossSelected = (lossName: string) => {
    // Null state means nothing is selected
    if (filterState.filterByTopLosses === null) {
      return false;
    }

    // Empty array means all are selected
    if (filterState.filterByTopLosses.length === 0) {
      return true;
    }

    // Otherwise directly check if this loss is in the filter list
    return filterState.filterByTopLosses.includes(lossName);
  };

  // Handle selection of a loss item
  const handleLossSelection = (lossName: string) => {
    startTransition(() => {
      setFilterState(p => {
        // Special case for the "All" option
        if (lossName === "SELECT_ALL") {
          // Toggle between all selected and no selection
          if (isAllSelected) {
            // If all were selected, clear selection
            return {
              ...p,
              filterByTopLosses: null, // null means no filter
            };
          } else {
            // Otherwise select all losses
            return {
              ...p,
              filterByTopLosses: [], // Empty array means all selected
            };
          }
        } else {
          // For a specific loss item
          // Handle null state (no filter) separately
          if (p.filterByTopLosses === null) {
            // If starting from null state, select just this item
            return {
              ...p,
              filterByTopLosses: [lossName],
            };
          }

          const currentFilters = p.filterByTopLosses;

          // If all losses are currently selected (empty array)
          if (currentFilters.length === 0) {
            // Select everything except this one
            return {
              ...p,
              filterByTopLosses: allLossNames.filter(name => name !== lossName),
            };
          }
          // If this loss is already selected, remove it
          else if (currentFilters.includes(lossName)) {
            const newFilters = currentFilters.filter(name => name !== lossName);
            // If no losses left, set to null to indicate no filter
            if (newFilters.length === 0) {
              return {
                ...p,
                filterByTopLosses: null,
              };
            }
            return {
              ...p,
              filterByTopLosses: newFilters,
            };
          }
          // Otherwise add this loss
          else {
            return {
              ...p,
              filterByTopLosses: [...currentFilters, lossName],
            };
          }
        }
      });
    });
  };

  return (
    <>
      <StyledMenuItemFilterControls
        className="filterTopLosses"
        value={"Filter by Top Losses"}
        style={{
          fontWeight: "bold",
          opacity: isTopLossesAllowed ? 1 : 0.5,
          pointerEvents: isTopLossesAllowed ? "auto" : "none",
          ...(activeMenu === "topLosses" && {
            backgroundColor: lightGreyBackground,
          }),
        }}
        onClick={e => {
          if (isTopLossesAllowed) {
            e.stopPropagation();
            handleClick(e);
          }
        }}
        onMouseEnter={handleMouseEnter}
      >
        <div
          style={{
            margin: "0 10px 0 -2px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Sort style={{ width: 18, color: lightNeutral70 }} />
        </div>
        <div className="label">{translate("Filter Top Losses...")}</div>
        <ArrowRight style={{ marginLeft: "auto" }} />
      </StyledMenuItemFilterControls>
      {anchorEl && <MouseoverMenuItemGlobalStyles />}
      <Menu
        id="process-tiles-top-losses-menu"
        hideBackdrop={true}
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && activeMenu === "topLosses"}
        onClose={handleClose}
        MenuListProps={{
          onMouseEnter: () => {
            /* prevent close during hover transition */
          },
          style: { pointerEvents: "auto" },
        }}
        disableAutoFocusItem
        keepMounted
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {/* All option at the top */}
        <MenuItem
          value={"All"}
          onClick={() => {
            handleLossSelection("SELECT_ALL");
            // Don't call handleClose to keep menu open
          }}
          style={{ display: "flex", alignItems: "center" }}
        >
          <div style={{ flexGrow: 1 }}>All</div>
          <div className="check-box">
            <Checkbox
              checked={isAllSelected}
              indeterminate={Boolean(
                filterState.filterByTopLosses !== null &&
                  filterState.filterByTopLosses.length > 0 &&
                  !isAllSelected
              )}
              size="small"
              style={{ padding: "0 0 0 16px", marginRight: -9 }}
            />
          </div>
        </MenuItem>

        {/* Divider after All */}
        <Divider style={{ marginTop: 0, marginBottom: 0 }} />

        {/* Categories with their losses */}
        {categories.map(categoryKey => {
          const lossesInCategory = byCategory[categoryKey] || [];
          const tagsInThisCategory = filteredTagList.filter(
            ({ name, pnlCategory }) => pnlCategory === categoryKey
          );

          if (lossesInCategory.length === 0) {
            return null;
          }

          const categoryName = CATEGORY_TO_LABEL_MAP[categoryKey];
          const categoryColor = PNL_COLORS[categoryKey];

          return [
            <br key={`${categoryKey}-br`} />,

            // Category Header
            <MenuItem
              key={categoryKey}
              style={{
                paddingTop: "0.5em !important",
                marginTop: "0.5em !important",
                pointerEvents: "none", // Categories are not selectable
                fontWeight: "bold",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  fontWeight: "bold",
                }}
              >
                <div
                  style={{
                    boxSizing: "border-box",
                    maxWidth: "12px",
                    maxHeight: "12px",
                    minWidth: "12px",
                    minHeight: "12px",
                    border: PNL_COLOR_SQUARE_BORDER,
                    marginRight: "0.5em",
                    background: categoryColor,
                  }}
                />
                <div>{categoryName}</div>
              </div>
            </MenuItem>,

            <Divider
              key={`${categoryKey}-divider`}
              style={{ marginTop: 0, marginBottom: 0 }}
            />,

            // Loss items within this category
            ...tagsInThisCategory.map((lossItem: LossItemDetails) => (
              <MenuItem
                key={`${categoryKey}-${lossItem.name}`}
                onClick={() => {
                  handleLossSelection(lossItem.name);
                  // Don't call handleClose to keep menu open
                }}
                style={{ display: "flex", alignItems: "center" }}
              >
                <div style={{ flexGrow: 1 }}>{lossItem.name}</div>
                <div className="check-box">
                  <Checkbox
                    checked={isLossSelected(lossItem.name)}
                    size="small"
                    style={{ padding: "0 0 0 16px", marginRight: -9 }}
                  />
                </div>
              </MenuItem>
            )),
          ];
        })}
      </Menu>
    </>
  );
}

function SortByDropdown({ startTransition }) {
  const [filterState, setFilterState] = useProcessTilesFilterState();
  const { translate } = useFlightTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = event => {
    setAnchorEl(p => (p ? null : event.currentTarget));
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const [isDarkMode] = useIsDarkMode();
  const active = !isEqual(
    filterState.sortBy,
    INITIAL_PROCESS_TILES_FILTER_STATE.sortBy
  );
  const dataDumpsFullRangeByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );
  const hasDataDumps = Object.keys(dataDumpsFullRangeByProcess).length > 0;

  const [
    {
      general: {
        expandedProcessTileAheadBehindProcesses: aheadBehindProcesses,
      },
    },
  ] = useFlightConfig();

  return (
    <>
      <Button
        aria-controls="process-tiles-sort-controls"
        aria-haspopup="true"
        className={`btnProcessTilesDropdown${active ? " active" : ""}`}
        onClick={handleClick}
        endIcon={<KeyboardArrowDown />}
        startIcon={<Sort />}
      >
        {translate(`Sort${active ? " by" : ""}`)} {filterState.sortBy?.label}
      </Button>
      <Menu
        id="process-tiles-sort-controls"
        anchorEl={anchorEl}
        keepMounted={false}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock={true}
        {...(isDarkMode
          ? {
              PaperProps: {
                style: {
                  background: darkModeModalBackgroundColor,
                  color: darkModeTextColor,
                },
              },
            }
          : {})}
        variant={"menu"} // Use "menu" to prevent selected items from impacting the initial focus and the vertical alignment relative to the anchor element.
      >
        {SORTABLE_METRICS.filter(
          ({ source }) => source === "askr" || hasDataDumps
        )
          // only customers with ahead/behind tiles enabled can select aheadBehind option
          .filter(({ customKey }) =>
            customKey === "aheadBehind" ? aheadBehindProcesses.length > 0 : true
          )

          .map(sortableMetric => {
            const sortOrder =
              filterState.sortBy?.label === sortableMetric.label
                ? filterState.sortBy?.order
                : null;
            return [
              <StyledMenuItemFilterControls
                value={sortableMetric.label}
                key={sortableMetric.label}
                onClick={() => {
                  startTransition(() => {
                    setFilterState(prev => {
                      // if we're grouped by pnlCategory and just clicked "sort by pnlCategory", ungroup first
                      const p = ungroupFirst(prev, sortableMetric);

                      const sortBy = { ...sortableMetric };
                      // if unsorted or not sorted by this label, sort descending
                      if (
                        !p.sortBy ||
                        p.sortBy?.label !== sortableMetric.label
                      ) {
                        return {
                          ...p,
                          sortBy: {
                            ...p.sortBy,
                            ...sortBy,
                            order: "desc",
                          },
                        };
                        // if already descending, sort ascending
                      } else if (
                        p.sortBy?.label === sortableMetric.label &&
                        p.sortBy?.order === "desc"
                      ) {
                        return {
                          ...p,
                          sortBy: {
                            ...p.sortBy,
                            ...sortBy,
                            order: "asc",
                          },
                        };
                        // if already ascending, unsort
                      } else {
                        return {
                          ...p,
                          sortBy: INITIAL_PROCESS_TILES_FILTER_STATE.sortBy,
                        };
                      }
                    });
                  });
                }}
                className={
                  filterState.sortBy?.customKey &&
                  sortableMetric.customKey === filterState.sortBy.customKey
                    ? "selected"
                    : ""
                }
              >
                <div className="label">{sortableMetric.label}</div>{" "}
                <IconButton
                  className={`sortArrowButton${
                    sortOrder === "asc" ? " active" : ""
                  }`}
                  style={{ marginLeft: 64, marginRight: -8 }}
                  onClick={e => {
                    e.stopPropagation();

                    // if "pnlCategorySeparated" or "groupSeparated" are selected, keep them selected and only switch the order
                    if (
                      filterState.sortBy?.customKey &&
                      ["pnlCategorySeparated", "groupSeparated"].includes(
                        filterState.sortBy.customKey
                      )
                    ) {
                      startTransition(() =>
                        setFilterState(p => ({
                          ...p,
                          sortBy: {
                            ...sortableMetric,
                            customKey: p.sortBy?.customKey,
                            order: "asc",
                          },
                        }))
                      );
                      return;
                    }

                    if (sortOrder === "asc") {
                      startTransition(() => {
                        setFilterState(prev => {
                          // if we're grouped by pnlCategory and just clicked "sort by pnlCategory", ungroup first
                          const p = ungroupFirst(prev, sortableMetric);
                          return {
                            ...p,
                            sortBy: null,
                          };
                        });
                      });
                    } else {
                      startTransition(() => {
                        setFilterState(prev => {
                          // if we're grouped by pnlCategory and just clicked "sort by pnlCategory", ungroup first
                          const p = ungroupFirst(prev, sortableMetric);
                          return {
                            ...p,
                            sortBy: { ...sortableMetric, order: "asc" },
                          };
                        });
                      });
                    }
                  }}
                >
                  <ArrowUpward />
                </IconButton>
                <IconButton
                  className={`sortArrowButton${
                    sortOrder === "desc" ? " active" : ""
                  }`}
                  onClick={e => {
                    e.stopPropagation();

                    // if "pnlCategorySeparated" or "groupSeparated" are selected, keep them selected and only switch the order
                    if (
                      filterState.sortBy?.customKey &&
                      ["pnlCategorySeparated", "groupSeparated"].includes(
                        filterState.sortBy.customKey
                      )
                    ) {
                      startTransition(() =>
                        setFilterState(p => ({
                          ...p,
                          sortBy: {
                            ...p.sortBy,
                            ...sortableMetric,
                            customKey: p.sortBy?.customKey,
                            order: "desc",
                          },
                        }))
                      );
                      return;
                    }

                    if (sortOrder === "desc") {
                      startTransition(() => {
                        setFilterState(prev => {
                          // if we're grouped by pnlCategory and just clicked "sort by pnlCategory", ungroup first
                          const p = ungroupFirst(prev, sortableMetric);
                          return {
                            ...p,
                            sortBy: INITIAL_PROCESS_TILES_FILTER_STATE.sortBy,
                          };
                        });
                      });
                    } else {
                      startTransition(() =>
                        setFilterState(prev => {
                          // if we're grouped by pnlCategory and just clicked "sort by pnlCategory", ungroup first
                          const p = ungroupFirst(prev, sortableMetric);
                          return {
                            ...p,
                            sortBy: {
                              ...p.sortBy,
                              ...sortableMetric,
                              order: "desc",
                            },
                          };
                        })
                      );
                    }
                  }}
                >
                  <ArrowDownward />
                </IconButton>
              </StyledMenuItemFilterControls>,
            ];
          })}
      </Menu>
    </>
  );
}

function ungroupFirst(
  prev: ProcessTilesFilterState,
  sortableMetric: SortableMetric
) {
  return prev.groupBy?.customKey === sortableMetric.customKey
    ? {
        ...prev,
        groupBy: INITIAL_PROCESS_TILES_FILTER_STATE.groupBy,
      }
    : prev;
}

/** Autocomplete dropdown search for filtering multiple processes */
function SearchProcessNamesInput({ startTransition }) {
  const visibleProcesses = useVisibleProcesses();
  const visibleProcessNames = useMemo(
    () => visibleProcesses.map(p => p.name),
    [visibleProcesses]
  );
  const { translate } = useFlightTranslation();
  const [
    { searchText, searchTextDisplay, selectedProcessNames },
    setFilterState,
  ] = useProcessTilesFilterState();

  return (
    <div className={`searchInput${searchText ? " active" : ""}`}>
      <SearchMultiSelect
        placeholderStr={searchTextDisplay || translate("Search for a process")}
        selectedItems={selectedProcessNames}
        setSelectedItems={selectedItems => {
          setFilterState(prev => ({ ...prev, searchTextDisplay: "" }));
          startProcessTilesPanelPending();
          startTransition(() => {
            const areAllCurrentlySelected =
              selectedProcessNames.length === visibleProcessNames.length;
            const shouldSelectAll =
              !areAllCurrentlySelected && selectedItems.includes(SELECT_ALL);
            const shouldSelectNone =
              areAllCurrentlySelected && selectedItems.includes(SELECT_ALL);
            setFilterState(p => ({
              ...p,
              searchText: null,
              selectedProcessNames: shouldSelectAll
                ? visibleProcessNames
                : shouldSelectNone
                  ? []
                  : selectedItems.filter(item => item !== SELECT_ALL),
            }));
          });
        }}
        title=""
        items={[SELECT_ALL, ...visibleProcessNames]}
        textValue={searchTextDisplay}
        AutocompleteProps={{
          renderOption: (
            props: HTMLAttributes<HTMLLIElement>,
            option: string
          ) => {
            const isSelectAll = option === SELECT_ALL;
            const areAllSelected =
              selectedProcessNames.length === visibleProcessNames.length ||
              selectedProcessNames.includes(SELECT_ALL);
            return (
              <StyledOption
                className={`option process-name-option option-${kebabCase(option)} ${
                  selectedProcessNames.includes(option) ? " selected" : ""
                }`}
                {...props}
              >
                <Checkbox
                  checked={
                    areAllSelected || selectedProcessNames.includes(option)
                  }
                />
                <div>{isSelectAll ? "Select All" : option}</div>
              </StyledOption>
            );
          },
          renderInput: params => (
            <TextField
              {...params}
              placeholder={
                searchTextDisplay || translate("Search for a process")
              }
              value={searchTextDisplay}
              InputProps={{
                ...params.InputProps,
                ...{
                  startAdornment: (
                    <Search
                      style={{
                        color: lightTextBody,
                      }}
                    />
                  ),
                  endAdornment: searchTextDisplay ? (
                    <IconButton
                      className="btnClearInput"
                      onClick={() => {
                        startProcessTilesPanelPending();
                        setFilterState(prev => ({
                          ...prev,
                          searchTextDisplay: "",
                        }));
                        startTransition(() => {
                          setFilterState(p => ({
                            ...p,
                            searchText: null,
                          }));
                        });
                      }}
                    >
                      <Close />
                    </IconButton>
                  ) : null,
                },
                onChange: e => {
                  // update the display value immediately
                  setFilterState(prev => ({
                    ...prev,
                    searchTextDisplay: e.target.value,
                  }));
                  startProcessTilesPanelPending();
                  // update the search results when it won't freeze the app
                  startTransition(() => {
                    stopProcessTilesPanelPending();

                    setFilterState(p => ({
                      ...p,
                      searchText: e.target.value,
                    }));
                  });
                },
              }}
            />
          ),
          filterOptions: (options: string[], { inputValue, ...rest }) => {
            // keep ones that include all input words
            const filteredOptions = options.filter(option =>
              Boolean(
                inputValue.split(" ").reduce((isMatchAcc, word) => {
                  // make sure each word in the input is included in the option
                  return Boolean(
                    isMatchAcc &&
                      option.toLowerCase().includes(word.toLowerCase())
                  );
                }, true)
              )
            );
            return filteredOptions;
          },
        }}
      />
    </div>
  );
}

export function startProcessTilesPanelPending() {
  document
    .querySelector(`[data-component-name="Process Tiles Panel"]`)
    ?.classList.add("pending");
}

export function stopProcessTilesPanelPending() {
  document
    .querySelector(`[data-component-name="Process Tiles Panel"]`)
    ?.classList.remove("pending");
}

const MouseoverMenuItemGlobalStyles = createGlobalStyle`
/* prevent the child menu's modal backdrop from blocking mouse events on the parent menu while child menu is open */
.MuiModal-root{
  pointer-events: none;
}
.MuiModal-root * {
  pointer-events: auto;
}
.MuiList-root, .MuiList-root *{
  pointer-events: auto;
}
`;
